import { ChatModMessage            ,
         CHAT_CATEGORY_LABELS      ,
         CHAT_SENSITIVE_CATEGORIES ,
         CHAT_FLAG_LABELS          } from '../../../dataTypes/moderationManagment/chat_types';

/**
 * Une couleur par chaîne, la même partout (rail, spectre, cartes, fil) : avec
 * plusieurs chaînes suivies, c'est elle qui dit d'un coup d'œil d'où vient un
 * message. Choisies pour rester distinctes sur le bleu nuit de la régie et ne
 * pas se confondre avec le vert / ambre / rouge du risque.
 */
const CHANNEL_COLORS = [ '#5EA8FF', '#C792EA', '#59D0E0', '#FF8FB1', '#E6D36A', '#9FB4FF', '#F2A65A', '#B7C66B' ];

export const channelColor = (channelId: number, orderedIds: number[]): string => {
  const index = orderedIds.indexOf(channelId);
  return CHANNEL_COLORS[(index >= 0 ? index : channelId) % CHANNEL_COLORS.length];
};

export const RISK = { ok: '#3FBF7F', doubt: '#F0B429', bad: '#F0565B' };

/** Couleur d'un score sur l'échelle de la chaîne (seuils du niveau). */
export const riskColor = (score: number | null, publish: number | null, reject: number | null): string => {
  if (score === null) return RISK.doubt;
  if (reject !== null && score >= reject) return RISK.bad;
  if (publish !== null && score < publish) return RISK.ok;
  return publish === null ? (score >= 0.6 ? RISK.bad : score >= 0.3 ? RISK.doubt : RISK.ok) : RISK.doubt;
};

export const pct = (score: number | null): string => score === null ? '—' : String(Math.round(score * 100));

export const secondsSince = (iso: string | null, now: number): number =>
  iso ? Math.max(0, Math.round((now - Date.parse(iso)) / 1000)) : 0;

/** « 8 s », « 2 min », « 1 h 05 ». */
export const shortDuration = (seconds: number): string => {
  if (seconds < 60)   return `${seconds} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h} h ${String(m).padStart(2, '0')}`;
};

export const clockTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

export interface MessageChip { key: string; label: string; kind: 'harm' | 'flag' | 'soft'; }

/** Ce qui a fait tiquer l'IA, du plus fort au plus faible, signaux locaux d'abord. */
export const messageChips = (message: ChatModMessage, max = 4): MessageChip[] => {
  const flags: MessageChip[] = message.aiFlags.map(flag => ({
    key: `f-${flag}`, label: CHAT_FLAG_LABELS[flag] ?? flag, kind: 'flag' as const,
  }));
  const categories: MessageChip[] = Object.entries(message.aiCategories)
    .filter(([, confidence]) => confidence >= 0.3)
    .sort((a, b) => b[1] - a[1])
    .map(([name, confidence]) => ({
      key   : `c-${name}`,
      label : `${CHAT_CATEGORY_LABELS[name] ?? name} ${Math.round(confidence * 100)}`,
      kind  : CHAT_SENSITIVE_CATEGORIES.has(name) ? 'soft' as const : 'harm' as const,
    }));
  return [...flags, ...categories].slice(0, max);
};

/** Découpe le texte pour surligner liens, e-mails et numéros (ce qui n'est pas publié sans vous). */
export const highlightParts = (text: string): { text: string; mark: boolean }[] => {
  const pattern = /(https?:\/\/\S+|www\.\S+|\b[\w-]{2,}\.(?:com|fr|net|org|io|tv|gg|ly|me|be|ch|co|xyz|link|shop)\b\S*|[\w.+-]+@[\w-]+\.[\w.]+|(?:\+?\d[\s.-]?){9,})/gi;
  const parts: { text: string; mark: boolean }[] = [];
  let last = 0;
  text.replace(pattern, (match, _g, offset: number) => {
    if (offset > last) parts.push({ text: text.slice(last, offset), mark: false });
    parts.push({ text: match, mark: true });
    last = offset + match.length;
    return match;
  });
  if (last < text.length) parts.push({ text: text.slice(last), mark: false });
  return parts;
};

/** Petit signal sonore à l'arrivée d'un message à valider (Web Audio, rien à charger). */
let audioContext: AudioContext | null = null;

export const playChime = () => {
  try {
    audioContext = audioContext ?? new (window.AudioContext || (window as any).webkitAudioContext)();
    const ctx = audioContext;
    if (ctx.state === 'suspended') { ctx.resume(); }
    [ 880, 1320 ].forEach((frequency, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      const t    = ctx.currentTime + i * 0.11;
      osc.type = 'sine';
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.12, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.2);
    });
  } catch { /* pas de son possible : l'écran suffit */ }
};

/**
 * Le son d'un nouveau signalement, distinct de celui du chat (deux notes
 * aiguës et brèves) : trois notes descendantes, plus graves et plus tenues,
 * au timbre plus rond (triangle). On reconnaît à l'oreille, sans regarder,
 * s'il s'agit d'un message à valider ou d'un contenu signalé.
 */
export const playReportChime = () => {
  try {
    audioContext = audioContext ?? new (window.AudioContext || (window as any).webkitAudioContext)();
    const ctx = audioContext;
    if (ctx.state === 'suspended') { ctx.resume(); }
    [ 784, 622, 523 ].forEach((frequency, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      const t    = ctx.currentTime + i * 0.17;
      osc.type = 'triangle';
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.16, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.36);
    });
  } catch { /* pas de son possible : la bannière suffit */ }
};
